/**
 * libraryStore.js
 * 
 * Reads and saves libraries in the SQL database, through PersonalBackend.
 * Built-in libraries live in the app and are merged in here.
 */

import { BackendError, callBackend } from "../../../config/backend";
import { builtInLibraries } from "./libraries";
import { generateRoomCode } from "../../../Rooms/helpers/createARoom";

const MAX_ATTEMPTS = 5;

/**
 * Libraries everyone can pick from: built-in ones, then defaults from the database
 * @returns {Array}     -   [{ code, name, description, itemCount }]
 */
export const fetchDefaultLibraries = async () => {
    try {
        const { libraries } = await callBackend("/auction/libraries/defaults");
        return [...builtInLibraries, ...libraries];
    } catch (error) {
        console.error("Error fetching default libraries:", error);
        return builtInLibraries;
    }
};

/**
 * A library by its ID (case-insensitive)
 * @param {String} code     -   Library ID
 * @returns {Object|null}   -   The library, or null if there isn't one
 */
export const fetchLibrary = async (code) => {
    const cleanCode = code.trim().toLowerCase();
    const builtIn = builtInLibraries.find((library) => library.code === cleanCode);
    if (builtIn) return builtIn;

    try {
        const { library } = await callBackend(`/auction/libraries/${encodeURIComponent(cleanCode)}`);
        return library;
    } catch (error) {
        if (error instanceof BackendError && error.status === 404) return null;
        console.error("Error fetching library:", error);
        return null;
    }
};

/**
 * Saves a new library with a generated ID
 * @param {Object} library  -   { name, description, nameAttribute, scoreAttribute, attributes, items, itemCount }
 * @param {Object} user     -   The signed-in user
 * @returns {Object|null}   -   The saved library (with its code), or null on failure
 */
export const saveLibrary = async (library, user) => {
    const { name, description, nameAttribute, scoreAttribute, attributes, items, itemCount } = library;

    // Codes are unique in the database, so try again if one is taken
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const code = generateRoomCode();
        try {
            await callBackend("/auction/libraries", {
                method  :   "POST",
                body    :   {
                    code, name, description, nameAttribute, scoreAttribute, attributes, items, itemCount,
                    createdByName   :   user?.displayName || null,
                },
            });
            return { ...library, code };
        } catch (error) {
            const isCodeTaken = error instanceof BackendError && error.status === 409;
            if (!isCodeTaken) {
                console.error("Error saving library:", error);
                return null;
            }
        }
    }
    return null;
};
